/** EditOpportunityTabs.jsx - the Summary and Quote tab bodies of the editor. */
import { useContext } from 'react';
import { fmt } from '../../utils/format';
import { AppContext } from '../../context/AppContext';
import { accountLabel, indexById } from '../crm/nextStep';
import { STATUS_LIST, NEEDS_REASON, computedSt, inputSt, labelSt, requiredSt, rowSt } from './editOpportunityConstants';
import { Section } from './EditOpportunityFields';
import { isProjectForm, showsField } from './editOpportunityForm';
import AddInvoiceRow from './AddInvoiceRow';
import ProjectInvoicesBox from './ProjectInvoicesBox';
import { DIVISIONS } from '../../data/divisions';
import { fmtMoney } from '../ksa/ksaMoney';
import { ContactSelect } from '../tasks/TaskComposer';
import TagPeople from '../tasks/TagPeople';
import MainProjectPicker from '../projects/hierarchy/MainProjectPicker';
import SearchSelect from '../ui/SearchSelect';

const fieldErr = { fontSize: 10, color: '#B71C1C', fontWeight: 600, marginTop: 3 };

/**
 * T-03 (Paul's 1.2 retest): a NEW opportunity needs its first task, the way a
 * new account or site does - what happens next, who owns it, when it is due,
 * and (when a site is linked) optionally who it is with. Shown only while
 * creating on the main tab; the editor checks it before saving and the server
 * saves the opportunity and the task together (first_task).
 *
 * `value` is { description, owner, due_date, contact_id, tagged }; `onChange(key, v)`;
 * `errors` holds firstTaskErrors' messages; `owners` are names; `contacts` are
 * the linked site's (nextStep.contactsForHome). Module scope, never defined in
 * a render body, so its inputs keep focus while typing.
 */
export function FirstTaskSection({ value, onChange, errors = {}, owners = [], contacts = [] }) {
  const err = (k) => (errors[k] ? <div style={fieldErr}>{errors[k]}</div> : null);
  const red = (k) => (errors[k] ? { border: '1px solid #B71C1C' } : null);
  return (
    <Section title="First task" icon="check" accent="#B07503">
      <div style={{ fontSize: 10.5, color: '#666', marginBottom: 4, lineHeight: 1.45 }}>
        Every new opportunity needs an open task, even a follow-up months out, so it never slips.
      </div>
      <div style={rowSt}>
        <div style={labelSt}>
          Task<span style={requiredSt}>*</span>
        </div>
        <div style={{ flex: 1, minWidth: 0 }}>
          <input
            style={{ ...inputSt, width: '100%', ...red('description') }}
            value={value.description}
            aria-label="First task"
            onChange={(e) => onChange('description', e.target.value)}
            placeholder="What happens next (e.g. Send the quote to the buyer)"
          />
          {err('description')}
        </div>
      </div>
      <div style={rowSt}>
        <div style={labelSt}>
          Owner<span style={requiredSt}>*</span>
        </div>
        <div style={{ flex: 1, minWidth: 0 }}>
          <select
            style={{ ...inputSt, width: '100%', ...red('owner') }}
            value={value.owner}
            aria-label="First task owner"
            onChange={(e) => onChange('owner', e.target.value)}
          >
            <option value="">Pick one...</option>
            {owners.map((n) => (
              <option key={n} value={n}>
                {n}
              </option>
            ))}
          </select>
          {err('owner')}
        </div>
      </div>
      <div style={rowSt}>
        <div style={labelSt}>
          Due date<span style={requiredSt}>*</span>
        </div>
        <div style={{ flex: 1, minWidth: 0 }}>
          <input
            type="date"
            style={{ ...inputSt, width: '100%', ...red('due_date') }}
            value={value.due_date}
            aria-label="First task due date"
            onChange={(e) => onChange('due_date', e.target.value)}
          />
          {err('due_date')}
        </div>
      </div>
      <div style={rowSt}>
        <div style={labelSt}>Tag people</div>
        <TagPeople
          value={value.tagged || []}
          onChange={(v) => onChange('tagged', v)}
          people={owners}
          owner={value.owner}
          label="First task: tag people"
          style={{ flex: 1 }}
        />
      </div>
      {contacts.length > 0 && (
        <div style={rowSt}>
          <div style={labelSt}>Contact (optional)</div>
          <ContactSelect contacts={contacts} value={value.contact_id} onChange={(v) => onChange('contact_id', v)} style={inputSt} />
        </div>
      )}
    </Section>
  );
}

/**
 * A-02 "Site / account": the exact CRM site this opportunity is for (Paul Q2 -
 * a Won opportunity makes that site a Customer). A searchable select over
 * nextStep.siteOptions (one SearchSelect). Module scope, never defined in a render body, so its
 * inputs keep focus while typing (see Section in EditOpportunityFields).
 *
 * A link that is not offered (a company with sites, a record gone from the
 * list) reads by the account's name from AppContext, never the raw id. The
 * context is read without useApp so the picker also renders on its own.
 * Left blank, the save links the account of the Customer typed - made from
 * that name when there is none (server/lib/accountLink.js, 10/11/26): every
 * opportunity has an account.
 */
const knownAccount = (app, id) => ((app && app.allAccounts) || []).some((a) => String(a.id) === String(id));

export function SitePicker({ options, value, onChange }) {
  const app = useContext(AppContext);
  const current = options.find((o) => o.id === value);
  const offList = () => {
    const byId = indexById((app && app.allAccounts) || []);
    return `${accountLabel(byId.get(String(value)), byId) || value}${knownAccount(app, value) ? ' (company)' : ' (not in the list)'}`;
  };
  return (
    <div style={rowSt}>
      <div style={labelSt}>Site / account</div>
      <div style={{ flex: 1, display: 'flex', minWidth: 0 }}>
        {/* a link to a record that is no longer offered still reads by name, so a save never drops it */}
        <SearchSelect
          options={options.map((o) => ({ value: o.id, label: o.label }))}
          value={value || ''}
          onChange={onChange}
          defaultLabel="- The customer's account -"
          valueLabel={value && !current ? offList() : ''}
          label="Site / account"
          title="The CRM site this opportunity is for"
          style={{ width: '100%' }}
        />
      </div>
    </div>
  );
}

// the stored projects.type vocabulary (mixed case, as the rows hold it)
export const OPPORTUNITY_TYPES = [
  'Paint Process',
  'Sealer Process',
  'Software',
  'Process',
  'Software/Process',
  'I/E',
  'Project',
  'Equipment',
  'Parts',
  'General',
];

/* The Summary tab of the record editor - an opportunity's and a project's
   (one record: the Status in the form decides which, editOpportunityForm
   isProjectForm). Fields that mean something at one stage only show at that
   stage (OPPORTUNITY_ONLY / PROJECT_ONLY there); a hidden field keeps its
   value. Topic and Name are one field, "Name" (10/8/26).
     siteLink    { options, value, onChange }, or null to hide the picker
                 (the GLOBAL KSA tab has no CRM accounts)
     firstTask   FirstTaskSection's props while a new opportunity is created
                 on the main tab (T-03), else null
     isCreate    a new record: Name is required (an old record without one
                 still saves - 11 have none)
     statuses    the Status choices (a new project: Won / Complete only)
     invoice     AddInvoiceRow's props on a project, else null (the KSA tab)
     ledger      ProjectInvoicesBox's props on a main-tab project: the
                 invoicing fields are then read only, worked out from the
                 invoice ledger (10/11/26)
     leftover    the Leftover's text (PO value - invoiced, worked out)
     oldList     [{ label, value }] from the old quote list, shown read-only
     files       the linked-files line (LinkedFiles), or null
     mainProject MainProjectPicker's props (the main tab), or null
   r49 (GlobalOps revisions 2), on both tabs: Customer is a searchable field,
   Engineer takes several people, Type offers Software/Process, and the
   quote's revision reads "Revision #" beside the quote number. */
// As many columns as fit (10/20/26): 3 in the wide pop-up (1366 px screens and
// up), then 2, then 1. CSS columns, not a grid, so a short section packs under
// another instead of leaving a gap beside a tall one (index.css .opp-summary-cols).
const SUMMARY_COLUMNS = { columns: '3 390px', columnGap: 12 };

export function renderSummaryTab({
  renderRow,
  siteLink,
  firstTask = null,
  f = {},
  isCreate = false,
  statuses = STATUS_LIST,
  invoice = null,
  ledger = null,
  leftover = '',
  oldList = [],
  files = null,
  mainProject = null,
}) {
  const show = (key) => showsField(key, f);
  const project = isProjectForm(f);
  return (
    <div className="opp-summary-cols" style={SUMMARY_COLUMNS}>
      {firstTask && (
        <div className="opp-span">
          <FirstTaskSection {...firstTask} />
        </div>
      )}
      {files && <div className="opp-span">{files}</div>}

      <Section title="Identification" icon="briefcase">
        {renderRow('Name', 'name', { required: isCreate, placeholder: 'e.g. Ford KTP TEMS RFQ' })}
        {renderRow('Customer', 'customer', { required: true, kind: 'customer' })}
        {siteLink && <SitePicker options={siteLink.options} value={siteLink.value} onChange={siteLink.onChange} />}
        {renderRow('Sub Customer', 'subCustomer')}
        {renderRow('Customer Project / RFQ #', 'customerProjectNumber')}
        {/* always shown, never required (r49): often issued after the PO */}
        {renderRow('Global Project Number', 'globalProjectNumber', { placeholder: 'e.g. P26041-010 (can be added later)' })}
        {/* main project / sub-project / PO line (10/14/26): automatic from the number, or picked */}
        {mainProject && <MainProjectPicker {...mainProject} />}
      </Section>

      <Section title="People" icon="user" accent="#1565C0">
        {renderRow('Owner', 'owner', { kind: 'owner' })}
        {renderRow('Approval Manager', 'approvalManager', { kind: 'ref', refKind: 'approval_manager' })}
        {renderRow('Engineer(s)', 'engineer', { kind: 'engineers', refKind: 'engineer' })}
        {show('pm') && renderRow('Project Manager', 'pm', { kind: 'ref', refKind: 'project_manager' })}
      </Section>

      <Section title="Quote" icon="dollar" accent="#2E7D32">
        {renderRow('Quote #', 'quoteLetter', { kind: 'quote' })}
        {renderRow('Revision #', 'rev', { placeholder: 'e.g. R1' })}
        {/* an opportunity without a quote date drops out of every list */}
        {renderRow('Quote Date', 'quoteDate', { required: !project, kind: 'date' })}
        {renderRow('Quote Type', 'quoteType', { kind: 'select', options: ['', 'Firm', 'Budgetary', 'ROM'] })}
        {show('quoteSent') && renderRow('Quote Sent', 'quoteSent', { kind: 'select', options: ['No', 'Yes'] })}
      </Section>

      <Section title="Classification" icon="reporting" accent="#6A1B9A">
        {renderRow('Status', 'status', { kind: 'select', options: statuses })}
        {NEEDS_REASON.includes(f.status) &&
          renderRow(`Why ${f.status}`, 'closeReason', {
            kind: 'textarea',
            required: true,
            placeholder:
              f.status === 'No Quote' ? 'Why we did not quote it' : `Why it was ${f.status.toLowerCase()} (who won, price, timing...)`,
          })}
        {/* Two values only. A row still carrying a retired value keeps it -
            the select branch in EditOpportunityFields adds it back as
            "(legacy)" so saving cannot blank it. */}
        {renderRow('Division', 'division', { kind: 'select', options: ['', ...DIVISIONS] })}
        {renderRow('Type', 'type', { kind: 'select', options: ['', ...OPPORTUNITY_TYPES] })}
        {show('probability') && renderRow('% Prob', 'probability', { kind: 'num', placeholder: '0-100' })}
      </Section>

      {!project && (
        <Section title="Dates" icon="calendar" accent="#E65100">
          {renderRow('Due Date', 'dueDate', { kind: 'date' })}
          {renderRow('Est. Close Date', 'estCloseDate', { kind: 'date' })}
          {renderRow('Book Date', 'bookDate', { kind: 'date' })}
        </Section>
      )}

      {project && (
        <Section title="PO" icon="briefcase" accent="#E65100">
          {renderRow('PO Number', 'poNumber')}
          {renderRow('PO Date Issued', 'poDateIssued', { kind: 'date' })}
          {renderRow('PO Value', 'poValue', { kind: 'num', placeholder: '0' })}
          {renderRow('Net Terms', 'terms', { placeholder: 'e.g. Net 30' })}
          {/* its own flag (projects.closed), not a Status: Complete never closed a job */}
          {renderRow('Active / Closed', 'closed', {
            kind: 'select',
            options: [
              ['', 'Active'],
              ['YES', 'Closed'],
            ],
          })}
          {renderRow('Bill Date', 'billDate', { kind: 'date' })}
        </Section>
      )}

      {project && (
        <Section title="Invoicing" icon="dollar" accent="#B07503">
          {ledger ? (
            <>
              {/* worked out from the invoice ledger: added below, corrected on the Finance Invoice tab */}
              {renderRow('Latest Invoice Date', 'latestInvoiceDate', {
                kind: 'computed',
                value: f.latestInvoiceDate ? String(f.latestInvoiceDate).slice(0, 10) : '',
                placeholder: 'From the invoices',
                title: 'The latest invoice date in the invoice ledger.',
              })}
              {renderRow("Invoice #'s", 'invoiceNumbers', {
                kind: 'computed',
                value: f.invoiceNumbers,
                placeholder: 'From the invoices',
                title: "The project's invoice numbers in the invoice ledger.",
              })}
              {renderRow('Invoiced (amount)', 'invoicedAlready', {
                kind: 'computed',
                value: f.invoicedAlready,
                placeholder: '0',
                title: "The sum of the project's invoices in the invoice ledger.",
              })}
            </>
          ) : (
            <>
              {renderRow('Latest Invoice Date', 'latestInvoiceDate', { kind: 'date' })}
              {renderRow("Invoice #'s", 'invoiceNumbers', { kind: 'grow', placeholder: 'e.g. 12345, 12346' })}
              {renderRow('Invoiced (amount)', 'invoicedAlready', { kind: 'num', placeholder: '0' })}
            </>
          )}
          {/* worked out, never typed: a typed leftover drifted the moment the invoiced amount changed */}
          {renderRow('Leftover (auto)', 'moneyLeftover', {
            kind: 'computed',
            value: leftover,
            placeholder: 'PO - Invoiced',
            title: 'PO Value minus Invoiced. Blank when fully invoiced.',
          })}
          {ledger ? <ProjectInvoicesBox {...ledger} /> : invoice && <AddInvoiceRow {...invoice} />}
        </Section>
      )}

      {project && oldList.length > 0 && (
        <Section title="From the old quote list" icon="reporting" accent="#757575">
          {oldList.map((x) => (
            <div key={x.label} style={rowSt}>
              <div style={labelSt}>{x.label}</div>
              <div style={{ flex: 1, minWidth: 0, fontSize: 12.5, fontWeight: 600, color: '#555', wordBreak: 'break-word' }}>{x.value}</div>
            </div>
          ))}
          <div style={{ marginTop: 6, fontSize: 10.5, color: '#888', lineHeight: 1.4 }}>
            Kept from the quote list's last import; not edited here.
          </div>
        </Section>
      )}

      <div className="opp-span">
        <Section title="Description" icon="reporting" accent="#757575">
          {renderRow('Description', 'description', { kind: 'textarea', placeholder: 'Add notes, scope, customer requirements…' })}
        </Section>
      </div>
    </div>
  );
}

/* The currency picker's labels, as stored: 'US Dollar' is USD (r44 added ZAR and INR). */
export const CURRENCY_OPTIONS = ['US Dollar', 'EUR', 'GBP', 'SAR', 'AED', 'ZAR', 'INR'];

/* Quote tab sections — inputs plus the computed (read-only) margin / go-get /
   anticipated fields, all fed from deriveQuoteMath.
   `ksaCurrency` (the GLOBAL KSA tab only) is the record's currency code: the
   picker reads "Currency (of the PO)" and the computed money carries that
   code instead of a $.
   `isProject` (Won / Complete in the form): Go / Get % and the anticipated
   value are a bid's forecast, so a project's quote tab leaves them out. */
export function renderQuoteTab({
  renderRow,
  totalCogs,
  grossMarginPct,
  quotedProfit,
  marginColor,
  goPct,
  getPct,
  goGetPct,
  anticipatedValue,
  ksaCurrency = null,
  isProject = false,
}) {
  const money = ksaCurrency ? (v) => fmtMoney(v, ksaCurrency) : fmt;
  return (
    <div style={{ display: 'grid', gridTemplateColumns: 'repeat(2, minmax(0, 1fr))', gap: 12 }}>
      <Section title="Quote Inputs" icon="dollar">
        {renderRow(ksaCurrency ? 'Currency (of the PO)' : 'Currency', 'currency', {
          required: true,
          kind: 'select',
          options: CURRENCY_OPTIONS,
        })}
        {renderRow('Billing Type', 'billingType', { kind: 'select', options: ['', 'Fixed FEE', 'T&L', 'T&M', 'Cost Plus'] })}
        {renderRow('Quoted Price', 'quotedPrice', { kind: 'num', placeholder: '0' })}
        {renderRow('Quoted Labor Cost', 'quotedLaborCost', { kind: 'num', placeholder: '0' })}
        {renderRow('Quoted Expenses', 'quotedExpenses', { kind: 'num', placeholder: '0' })}
      </Section>

      <Section title="Computed Margin" icon="trending">
        <div style={rowSt}>
          <div style={labelSt}>Total Quoted COGS</div>
          <input style={computedSt} value={totalCogs > 0 ? money(totalCogs) : ''} readOnly placeholder="auto-computed" />
        </div>
        <div style={rowSt}>
          <div style={labelSt}>Gross Margin %</div>
          <input
            style={{ ...computedSt, color: marginColor }}
            value={grossMarginPct != null ? `${grossMarginPct.toFixed(2)}%` : ''}
            readOnly
            placeholder={totalCogs > 0 ? 'auto-computed' : 'not costed - enter labor / expenses'}
          />
        </div>
        <div style={rowSt}>
          <div style={labelSt}>Quoted Profit</div>
          <input style={computedSt} value={quotedProfit && totalCogs ? money(quotedProfit) : ''} readOnly placeholder="auto-computed" />
        </div>
        <div
          style={{
            marginTop: 6,
            padding: '6px 10px',
            background: '#FAFBF7',
            borderRadius: 6,
            fontSize: 10.5,
            color: '#666',
            lineHeight: 1.4,
          }}
        >
          Margin auto-derives from Price − (Labor + Expenses). GAT target ≥30% (green).
        </div>
      </Section>

      {!isProject && (
        <Section title="Sales Confidence" icon="check" accent="#1565C0">
          {renderRow('Go %', 'goPct', { kind: 'num', placeholder: '0' })}
          {renderRow('Get %', 'getPct', { kind: 'num', placeholder: '0' })}
          <div
            style={{
              marginTop: 6,
              padding: '6px 10px',
              background: '#FAFBF7',
              borderRadius: 6,
              fontSize: 10.5,
              color: '#666',
              lineHeight: 1.4,
            }}
          >
            <b style={{ color: '#1565C0' }}>Go %</b> = probability we'll bid · <b style={{ color: '#1565C0' }}>Get %</b> = probability we'll
            win.
          </div>
        </Section>
      )}

      {!isProject && (
        <Section title="Anticipated Value" icon="dollar" accent="#E65100">
          <div style={rowSt}>
            <div style={labelSt}>Go × Get %</div>
            <input
              style={computedSt}
              value={goPct > 0 && getPct > 0 ? `${goGetPct.toFixed(2)}%` : ''}
              readOnly
              placeholder="auto-computed"
            />
          </div>
          <div style={rowSt}>
            <div style={labelSt}>Anticipated Value ({ksaCurrency || '$'})</div>
            <input
              style={{ ...computedSt, color: '#E65100', fontWeight: 800 }}
              value={anticipatedValue > 0 ? money(anticipatedValue) : ''}
              readOnly
              placeholder="auto-computed"
            />
          </div>
          <div
            style={{
              marginTop: 6,
              padding: '6px 10px',
              background: '#FFF8E1',
              borderRadius: 6,
              fontSize: 10.5,
              color: '#92400E',
              lineHeight: 1.4,
            }}
          >
            Anticipated = Quoted Price × Go% × Get%. Feeds the Est. Revenue card above.
          </div>
        </Section>
      )}
    </div>
  );
}

/* Footer (Delete / Comment / Cancel / Save) — plain render function. */
